/**
 * ModuleStatusIndicator - Renders sub-icons on module icon containers:
 * - Status: Greek letter (β/α) for beta/alpha maturity
 * - Category: small icon for master_data (Database), internal (Lock), technical (Wrench)
 * Wrap around the icon container div to overlay the indicator(s).
 */

import { Database, Lock, Shield, Wrench } from 'lucide-react';
import React from 'react';
import { cn } from '../../lib/utils';
import type { ModuleCategory, ModuleStatus } from '../config/modules.config';

interface ModuleStatusIndicatorProps {
  status: ModuleStatus;
  category?: ModuleCategory;
  children: React.ReactNode;
  /** Size variant: 'sm' for module switcher grid, 'md' for sidebar rail */
  size?: 'sm' | 'md';
}

const GREEK_LETTERS: Record<string, string> = {
  beta: 'β',
  alpha: 'α',
};

const CATEGORY_CONFIG: Record<ModuleCategory, { icon: React.ElementType; colorClass: string }> = {
  master_data: { icon: Database, colorClass: 'text-category-master-data' },
  internal: { icon: Lock, colorClass: 'text-category-internal' },
  technical: { icon: Wrench, colorClass: 'text-category-technical' },
  admin: { icon: Shield, colorClass: 'text-category-admin' },
};

export function ModuleStatusIndicator({ status, category, children, size = 'md' }: ModuleStatusIndicatorProps) {
  const hasStatusIndicator = status !== 'stable';
  const categoryConfig = category ? CATEGORY_CONFIG[category] : null;

  if (!hasStatusIndicator && !categoryConfig) {
    return <>{children}</>;
  }

  const iconSize = size === 'md' ? 'h-[9px] w-[9px]' : 'h-[8px] w-[8px]';

  return (
    <div className="relative">
      {children}
      {hasStatusIndicator && (
        <span
          className={cn(
            'absolute font-bold leading-none select-none pointer-events-none',
            size === 'md' && '-bottom-0.5 -right-1 text-[9px]',
            size === 'sm' && '-bottom-0.5 -right-0.5 text-[8px]',
            status === 'beta' && 'text-info-strong',
            status === 'alpha' && 'text-warning-strong',
          )}
        >
          {GREEK_LETTERS[status]}
        </span>
      )}
      {categoryConfig && (
        <span
          className={cn(
            'absolute select-none pointer-events-none',
            categoryConfig.colorClass,
            hasStatusIndicator
              ? (size === 'md' ? '-bottom-0.5 -left-1' : '-bottom-0.5 -left-0.5')
              : (size === 'md' ? '-bottom-0.5 -right-1' : '-bottom-0.5 -right-0.5'),
          )}
        >
          <categoryConfig.icon className={iconSize} />
        </span>
      )}
    </div>
  );
}
